import { memo, useCallback, useMemo, useState } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Tooltip,
  Button,
} from '@fluentui/react-components';
import {
  ArrowRepeatAllFilled,
  ArrowRepeatAllRegular,
  CheckmarkCircleFilled,
  ChevronLeftRegular,
  ChevronRightRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import { useForEachConstraint } from '@/hooks/useForEachConstraint';
import { useHandleLayout } from './useHandlePositions';
import { useIsHorizontalLayout } from '@/hooks/useLayoutOrientation';
import { ExecutionOverlay } from '@/components/shared';
import {
  FloatingNode,
  NodeHoverToolbar,
  useDetailLevel,
  type NodeStatus,
} from './FloatingNode';
import { getBuiltInManifest } from '@/data/manifests';
import type { NodeExecutionStatus } from '@/types/monitoring';

export type FloatingForeachNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  // Monitoring mode props
  executionStatus?: NodeExecutionStatus;
  executionDuration?: number;
  /** Number of loop iterations (for monitoring mode display) */
  iterationCount?: number;
  /** Currently viewed iteration index (for monitoring pager) */
  currentIteration?: number;
  /** Callback when iteration changes */
  onIterationChange?: (scopeName: string, iteration: number) => void;
  // Toolbar action callbacks (injected by WorkflowDesigner)
  onOpen?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onEditWithAI?: () => void;
};

export type FloatingForeachNodeType = Node<FloatingForeachNodeData, 'floatingForeach'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  // Container for loop/done outputs (vertical layout - below node)
  outputsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalXL,
    marginTop: tokens.spacingVerticalS,
  },
  // Individual output wrapper
  outputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    position: 'relative',
  },
  // Output label styling
  outputLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1',
    display: 'flex',
    alignItems: 'center',
    // Sub-token intentional spacing: no Fluent token matches 3px
    gap: '3px',
  },
  loopLabel: {
    color: tokens.colorPaletteMarigoldForeground2,
  },
  doneLabel: {
    color: tokens.colorPaletteGreenForeground1,
  },
  // Loop indicator icon (small circular arrow)
  loopIndicator: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorPaletteMarigoldForeground2,
  },
  doneIndicator: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorPaletteGreenForeground1,
  },
  // Handle styling with expanded hit area for accessibility
  handleBase: {
    width: '12px !important',
    height: '12px !important',
    borderRadius: '50%',
    position: 'relative',
    transform: 'none',
    top: '0',
    left: '0',
    cursor: 'crosshair',
    // Expand hit area to 44px for touch accessibility
    '::before': {
      content: '""',
      position: 'absolute',
      width: '44px',
      height: '44px',
      left: '-16px',
      top: '-16px',
      borderRadius: '50%',
    },
  },
  loopHandle: {
    backgroundColor: `${tokens.colorBrandBackground2} !important`,
    border: `2px solid ${tokens.colorBrandStroke1} !important`,
  },
  doneHandle: {
    backgroundColor: `${tokens.colorPaletteGreenBackground2} !important`,
    border: `2px solid ${tokens.colorPaletteGreenBorder2} !important`,
  },
  // Horizontal mode handles - small dots positioned on the icon
  handleHorizontal: {
    width: '10px !important',
    height: '10px !important',
    borderRadius: '50%',
  },
  // Visually hide loop/done handles in monitoring mode. They must stay in
  // the DOM so ReactFlow can anchor edges to them, but the colored dots
  // (orange loop / green done) are design-time affordances that distract
  // from the run state. Pointer events are also disabled so users can't
  // accidentally interact with hidden handles.
  handleHiddenInMonitoring: {
    backgroundColor: 'transparent !important',
    border: 'none !important',
    boxShadow: 'none !important',
    pointerEvents: 'none',
    '::before': {
      display: 'none',
    },
  },
  // Iteration pager for monitoring mode
  iterationPager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalSNudge,
  },
  iterationText: {
    fontSize: tokens.fontSizeBase200, // 12px — meets WCAG 1.4.4 resize/zoom requirements
    color: tokens.colorNeutralForeground3,
    minWidth: '60px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  iterationButton: {
    minWidth: '24px',
    minHeight: '24px',
    padding: tokens.spacingHorizontalXS,
    height: 'auto',
  },
  iterationIcon: {
    fontSize: tokens.fontSizeBase200,
  },
  // "Add to loop" hover button.
  // Extended hit-area (≈ 36px square) via ::before pseudo-element prevents
  // the button from disappearing as the cursor approaches it.
  addLoopButton: {
    position: 'absolute',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    padding: 0,
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1.5px solid ${tokens.colorPaletteMarigoldBorder2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transitionProperty: 'opacity, background-color, border-color, transform',
    transitionDuration: '0.15s',
    zIndex: 10,
    ':hover': {
      backgroundColor: tokens.colorPaletteMarigoldBackground2,
      border: `1.5px solid ${tokens.colorPaletteMarigoldForeground2}`,
    },
    // Invisible 36px hit area centered on the button — keeps hover/focus
    // alive when the cursor approaches from the node, and gives a forgiving
    // click target.
    '::before': {
      content: '""',
      position: 'absolute',
      width: '36px',
      height: '36px',
      left: '-8px',
      top: '-8px',
      borderRadius: '50%',
    },
  },
  addLoopButtonVisible: {
    opacity: 1,
  },
  // Horizontal mode: anchor to the upper-right of the diamond so the button
  // sits OUTSIDE the centered NodeHoverToolbar (top: -48px, centered).
  addLoopButtonHorizontal: {
    top: '-10px',
    left: '50px',
    ':hover': {
      transform: 'scale(1.1)',
    },
  },
  addLoopIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteMarigoldForeground2,
  },
  // Vertical-mode clickable chip variant of the loop label — used when the
  // loop body is empty so the entire chip becomes the add target.
  loopChipButton: {
    border: 'none',
    background: 'transparent',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transitionProperty: 'background-color',
    transitionDuration: '0.12s',
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        backgroundColor: tokens.colorNeutralBackground1Hover,
      },
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
  },
  loopChipAddIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteMarigoldForeground2,
    marginRight: tokens.spacingHorizontalXXS,
  },
  loopInfoBadge: {
    position: 'absolute',
    top: '-6px',
    right: '-10px',
    transform: 'scale(0.85)',
    width: '18px',
    height: '18px',
    opacity: 0,
    transitionProperty: 'opacity, transform',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'ease',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  loopInfoBadgeVisible: {
    opacity: 0.65,
    transform: 'scale(1)',
    pointerEvents: 'auto',
  },
  loopInfoBadgeFocusVisible: {
    ':focus-visible': {
      opacity: 0.65,
      transform: 'scale(1)',
      pointerEvents: 'auto',
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  loopInfoIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  handleLoopPosition: {
    left: '50%',
  },
  handleDonePosition: {
    top: '28px',
  },
});

/**
 * Compact floating-style Foreach Node with loop/done outputs.
 * Shows icon + label with loop indicator on the "loop" output.
 */
export const FloatingForeachNode = memo(
  ({ data, selected }: NodeProps<FloatingForeachNodeType>) => {
    const styles = useStyles();
    const handles = useHandleLayout();
    const detailLevel = useDetailLevel();
    const isHorizontal = useIsHorizontalLayout();

    const builtInManifest = useMemo(() => getBuiltInManifest(data.actionType), [data.actionType]);
    const brandColor = builtInManifest?.properties?.brandColor as string | undefined;

    // Monitoring mode
    const isMonitoringMode = data.executionStatus !== undefined;
    const iterationCount = data.iterationCount ?? 0;
    const currentIteration = data.currentIteration ?? 0;
    const hasMultipleIterations = iterationCount > 1;

    // Hover state for the add-loop button
    const [isHovered, setIsHovered] = useState(false);

    // ForEach constraint info
    const { hasAction } = useForEachConstraint(data.actionName);

    // Map execution status to FloatingNode status
    const getNodeStatus = (): NodeStatus => {
      if (!data.executionStatus) return 'default';

      switch (data.executionStatus) {
        case 'running':
          return 'running';
        case 'succeeded':
          return 'success';
        case 'failed':
          return 'failed';
        case 'skipped':
          return 'skipped';
        default:
          return 'default';
      }
    };

    // Extract stable refs for callbacks to avoid stale closures
    const { actionName, onIterationChange } = data;

    const handlePrevIteration = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentIteration > 0) {
          onIterationChange?.(actionName, currentIteration - 1);
        }
      },
      [actionName, onIterationChange, currentIteration]
    );

    const handleNextIteration = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentIteration < iterationCount - 1) {
          onIterationChange?.(actionName, currentIteration + 1);
        }
      },
      [actionName, onIterationChange, currentIteration, iterationCount]
    );

    // Arrow-key navigation when the iteration pager has focus.
    // ArrowLeft/ArrowRight move between iterations without requiring
    // the user to Tab to each button individually (WCAG 2.1.1 + APG carousel).
    const handlePagerKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft' && currentIteration > 0) {
          e.preventDefault();
          onIterationChange?.(actionName, currentIteration - 1);
        } else if (e.key === 'ArrowRight' && currentIteration < iterationCount - 1) {
          e.preventDefault();
          onIterationChange?.(actionName, currentIteration + 1);
        }
      },
      [actionName, onIterationChange, currentIteration, iterationCount]
    );

    return (
      <div
        className={styles.wrapper}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocusCapture={() => setIsHovered(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node)) {
            setIsHovered(false);
          }
        }}
      >
        <NodeHoverToolbar
          onConfigure={data.onOpen}
          onDelete={data.onDelete}
          onDuplicate={data.onDuplicate}
          onEditWithAI={data.onEditWithAI}
          showToolbar={!isMonitoringMode}
          disabled={selected}
        >
          <FloatingNode
            icon={<ArrowRepeatAllFilled aria-hidden="true" />}
            label={data.label}
            subLabel={data.actionType === 'Until' ? 'Until' : 'For Each'}
            detailLevel={detailLevel}
            selected={selected}
            status={getNodeStatus()}
            accentColor="purple"
            brandColorOverride={brandColor}
            shape="diamond"
            ariaLabel={`${data.label} ${data.actionType === 'Until' ? 'until' : 'foreach'} loop node${iterationCount > 0 ? `, ${iterationCount} iterations` : ''}`}
          >
            <Handle
              type="target"
              position={handles.input.position}
              id="input"
              isConnectable={true}
              style={handles.input.style}
            />
            {/* Horizontal mode: loop handle on top, done handle on right */}
            {isHorizontal && (
              <>
                <Tooltip content="Loop body — actions here run for each item" relationship="description" positioning="above">
                  <Handle
                    type="source"
                    position={Position.Top}
                    id="loop"
                    isConnectable={!isMonitoringMode}
                    className={mergeClasses(
                      styles.handleHorizontal,
                      styles.loopHandle,
                      styles.handleLoopPosition,
                      isMonitoringMode && styles.handleHiddenInMonitoring,
                    )}
                    aria-label="Loop body output"
                  />
                </Tooltip>
                <Tooltip content="Done — continues after all iterations complete" relationship="description" positioning="after">
                  <Handle
                    type="source"
                    position={Position.Right}
                    id="done"
                    isConnectable={!isMonitoringMode}
                    className={mergeClasses(
                      styles.handleHorizontal,
                      styles.doneHandle,
                      styles.handleDonePosition,
                      isMonitoringMode && styles.handleHiddenInMonitoring,
                    )}
                    aria-label="Loop complete output"
                  />
                </Tooltip>
              </>
            )}
            {/* Info badge when ForEach already has an action (at max capacity) */}
            {!isMonitoringMode && hasAction && (
              <Tooltip content="This loop contains one action (maximum). Use a sub-workflow for multiple steps." relationship="description" positioning="above">
                <div
                  className={mergeClasses(styles.loopInfoBadge, isHovered && styles.loopInfoBadgeVisible, styles.loopInfoBadgeFocusVisible)}
                  role="status"
                  aria-label="ForEach loop is at maximum capacity of one action"
                  tabIndex={hasAction && !isMonitoringMode ? 0 : -1}
                >
                  <InfoRegular className={styles.loopInfoIcon} />
                </div>
              </Tooltip>
            )}
          </FloatingNode>
        </NodeHoverToolbar>

        {/* Loop/Done outputs - vertical layout (below node).
            Empty loop body shows its + affordance through the TerminalEdge
            (a brand-colored dashed stub-edge ending in a + button), so the
            inline loop label here is display-only. */}
        {!isHorizontal && (
          <div className={styles.outputsContainer}>
            {/* Loop output with indicator */}
            <div className={styles.outputWrapper}>
              <span className={mergeClasses(styles.outputLabel, styles.loopLabel)}>
                <ArrowRepeatAllRegular className={styles.loopIndicator} aria-hidden="true" />
                loop
              </span>
              <Tooltip content="Loop body — actions here run for each item" relationship="description" positioning="after">
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id="loop"
                  isConnectable={!isMonitoringMode}
                  className={mergeClasses(
                    styles.handleBase,
                    styles.loopHandle,
                    isMonitoringMode && styles.handleHiddenInMonitoring,
                  )}
                  aria-label="Loop body output"
                />
              </Tooltip>
            </div>

            {/* Done output */}
            <div className={styles.outputWrapper}>
              <span className={mergeClasses(styles.outputLabel, styles.doneLabel)}>
                <CheckmarkCircleFilled className={styles.doneIndicator} aria-hidden="true" />
                done
              </span>
              <Tooltip content="Done — continues after all iterations complete" relationship="description" positioning="after">
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id="done"
                  isConnectable={!isMonitoringMode}
                  className={mergeClasses(
                    styles.handleBase,
                    styles.doneHandle,
                    isMonitoringMode && styles.handleHiddenInMonitoring,
                  )}
                  aria-label="Loop complete output"
                />
              </Tooltip>
            </div>
          </div>
        )}

        {/* Iteration pager for monitoring mode */}
        {isMonitoringMode && hasMultipleIterations && (
          <div
            className={styles.iterationPager}
            role="group"
            aria-label="Iteration navigation"
            onKeyDown={handlePagerKeyDown}
          >
            <Tooltip content="Previous iteration" relationship="description" positioning="below">
              <Button
                className={styles.iterationButton}
                appearance="subtle"
                size="small"
                icon={<ChevronLeftRegular className={styles.iterationIcon} />}
                onClick={handlePrevIteration}
                disabled={currentIteration === 0}
                aria-label="Previous iteration"
              />
            </Tooltip>
            <span
              className={styles.iterationText}
              aria-live="polite"
              aria-label={`Iteration ${currentIteration + 1} of ${iterationCount}`}
            >
              {currentIteration + 1}/{iterationCount}
            </span>
            <Tooltip content="Next iteration" relationship="description" positioning="below">
              <Button
                className={styles.iterationButton}
                appearance="subtle"
                size="small"
                icon={<ChevronRightRegular className={styles.iterationIcon} />}
                onClick={handleNextIteration}
                disabled={currentIteration >= iterationCount - 1}
                aria-label="Next iteration"
              />
            </Tooltip>
          </div>
        )}

        {/* Execution overlay */}
        {isMonitoringMode && (
          <ExecutionOverlay
            status={data.executionStatus!}
            duration={data.executionDuration}
            iterationCount={data.iterationCount}
            position="top-right"
          />
        )}
      </div>
    );
  }
);

FloatingForeachNode.displayName = 'FloatingForeachNode';
